import { DeviceType } from '@andromeda/util-environment';

enum ReleaseType {
    DEVELOPMENT = 'development',
    PRODUCTION = 'production',
    QA = 'qa',
}

interface IShoppableSite {
    /**
     * Site enum to keep track
     */
    site: Site;
    /**
     * Which environment the site is shoppable
     */
    release: ReleaseType;
    /**
     * The grocery server endpoint URL used to fetch item ids for recipe products
     */
    groceryServerEndpoint: string;
    /**
     * The site's ingredient ids for ingredients we want to ignore like water/ice
     */
    ignoredIngredientIds: string[];
    /**
     * The ids used to call Grocery Server unique to desktop, mobile and tablets
     */
    platformIds: any;
    /**
     * The grocery server source id unique to the site
     */
    recipeSourceId: number;
    /**
     * The primary color that should be used for the Shoppable Recipes module
     */
    primaryColor: string;
    /**
     * The secondary color that should be used for the Shoppable Recipes module
     */
    secondaryColor: string;
    /**
     * The font family that should be used for the Shoppable Recipes module
     */
    fontFamily: string;
}

enum Site {
    ALLRECIPES = 'allrecipes',
    BETTER_HOMES_GARDENS = 'bhg',
    EATING_WELL = 'eatingwell',
    FOOD_AND_WINE = 'foodandwine',
    HEALTH = 'health',
    MARTHA_LIVING = 'marthastewart',
    MIDWEST_LIVING = 'midwestliving',
    MYRECIPES = 'myrecipes',
    PEOPLE = 'people',
    PEOPLE_ESP = 'peopleenespanol',
    PARENTS = 'parents',
    RACHAEL_RAY_MAG = 'rachaelraymag',
    REAL_SIMPLE = 'realsimple',
    SOUTHERN_LIVING = 'southernliving',
    SHAPE = 'shape',
    UNKNOWN = 'unknown',
}

/**
 * Contains all sites that Local Offers can be used on
 */

const shoppableSites = new Map<string, IShoppableSite>();

shoppableSites.set(Site.ALLRECIPES, {
    site: Site.ALLRECIPES,
    groceryServerEndpoint: 'https://allrecipes.groceryserver.com/groceryserver',
    ignoredIngredientIds: ['2496'],
    platformIds: {
        [DeviceType.DESKTOP]: '36381536f4fa5e641cc6649a4ad959ec',
        [DeviceType.MOBILE]: 'dd7f11c8b986d3f433a6486030da579e',
        [DeviceType.TABLET]: 'ee7e8854a969ce746a4d792684ff66b5',
    },
    recipeSourceId: 224,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#d54215',
    secondaryColor: '#f5f6ea',
    fontFamily: "'Copper Pot', 'Source Sans Pro', sans-serif",
});

shoppableSites.set(Site.BETTER_HOMES_GARDENS, {
    site: Site.BETTER_HOMES_GARDENS,
    groceryServerEndpoint: 'https://bhg.groceryserver.com/groceryserver',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '52775a1a2d9f31aa096fe9ee5701c589',
        [DeviceType.MOBILE]: 'e5a3053e31622577da29f6bc5d693f2d',
        [DeviceType.TABLET]: '48024c54ff524f0505b7018fad9e3415',
    },
    recipeSourceId: 263,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#c83aa2',
    secondaryColor: '#f1f2ff',
    fontFamily: "'MB Empire', serif",
});

shoppableSites.set(Site.EATING_WELL, {
    site: Site.EATING_WELL,
    groceryServerEndpoint: 'https://eatingwell.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '9f0d5dbf0667334db2479ee1a5c6b4a2',
        [DeviceType.MOBILE]: '198a5cc7d2a857a3b647317e8e6cad0a',
        [DeviceType.TABLET]: '8c021b7677b9e51782c94ee873cfa0d8',
    },
    recipeSourceId: 257,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: 'rgb(99, 154, 58)',
    secondaryColor: '#f5f6ea',
    fontFamily: "'Roboto', sans-serif",
});

shoppableSites.set(Site.FOOD_AND_WINE, {
    site: Site.FOOD_AND_WINE,
    groceryServerEndpoint: 'https://foodandwine.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '933ddc2b44f97ffd01ce7ce03aaf81f4',
        [DeviceType.MOBILE]: '120e3499685d9edba59ca2ccf60f3d9c',
        [DeviceType.TABLET]: '042cdd50cce9ba602014d998ab356d06',
    },
    recipeSourceId: 272,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#e71f3d',
    secondaryColor: '#e4f3f6',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.HEALTH, {
    site: Site.HEALTH,
    groceryServerEndpoint: 'https://health.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        'dfae4acad8dc26b42297119bf0866c55', // Water
        'aa4671be2814999b547f258f7b140d17', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '95df8b444200a0a3652f31f96e35da90',
        [DeviceType.MOBILE]: '4d16f4538745e036077dc80ed12f0ca2',
        [DeviceType.TABLET]: '0e3b48269aea92366985c552acba1cd8',
    },
    recipeSourceId: 277,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#1a7f82', // Teal
    secondaryColor: '#fbd9ce', // Salmon
    fontFamily: "'Libre Franklin', sans-serif",
});

shoppableSites.set(Site.MARTHA_LIVING, {
    site: Site.MARTHA_LIVING,
    groceryServerEndpoint: 'https://marthastewart.groceryserver.com/groceryserver',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: 'fb544dc1c3bdabeae82aa18681b1e9fb',
        [DeviceType.MOBILE]: '1e6e6558baaa8940f4597e0d0735bc79',
        [DeviceType.TABLET]: 'e8dd7c7a81ab26016358f5f3d4ea3f14',
    },
    recipeSourceId: 255,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#99261e',
    secondaryColor: '#f5f6ea',
    fontFamily: "'Libre Franklin', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.MIDWEST_LIVING, {
    site: Site.MIDWEST_LIVING,
    groceryServerEndpoint: 'https://midwestliving.groceryserver.com/groceryserver',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '2df6c37e7ba46be60da5a1ec5ccfd9d4',
        [DeviceType.MOBILE]: 'ce69c31a45f9df6f12052ef79dd46728',
        [DeviceType.TABLET]: 'a5c73ebfb52a2db083205d29e2de3f0a',
    },
    recipeSourceId: 268,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#d54215',
    secondaryColor: '#f44336;',
    fontFamily: "'Klein', Arial, sans-serif",
});

shoppableSites.set(Site.MYRECIPES, {
    site: Site.MYRECIPES,
    groceryServerEndpoint: 'https://myrecipes.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '07f685b3f90f720dba1832e31fbd30d7',
        [DeviceType.MOBILE]: 'fd35fe8b30c3c8bc5465faf741ef99c3',
        [DeviceType.TABLET]: '09f3d6888e3dbfaddc35bdbb62801178',
    },
    recipeSourceId: 274,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#006165',
    secondaryColor: 'rgb(153,198,199)',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.PEOPLE, {
    // NOTE: All these ids are incorrect. This is just being used for Persistent Cart NLPs
    site: Site.PEOPLE,
    groceryServerEndpoint: 'https://people.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: 'f8eb0ed91c43df7d474082b690db0615',
        [DeviceType.MOBILE]: '28939c7f148d63f257c8409a32deb5a7',
        [DeviceType.TABLET]: '008e3be934e969e23b66a602e39e59b4',
    },
    recipeSourceId: 273,
    release: ReleaseType.PRODUCTION,
    primaryColor: '#20B1EA',
    secondaryColor: '#a054a0',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.PEOPLE_ESP, {
    site: Site.PEOPLE_ESP,
    groceryServerEndpoint: 'https://peopleenespanol.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: 'f8eb0ed91c43df7d474082b690db0615',
        [DeviceType.MOBILE]: '28939c7f148d63f257c8409a32deb5a7',
        [DeviceType.TABLET]: '008e3be934e969e23b66a602e39e59b4',
    },
    recipeSourceId: 273,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#006165',
    secondaryColor: 'rgb(153,198,199)',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.PARENTS, {
    site: Site.PARENTS,
    groceryServerEndpoint: 'https://parents.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '8135eb8bfaab54d8e4771422f4a25219',
        [DeviceType.MOBILE]: 'a75ba8c960498964e94bcac60e40abd4',
        [DeviceType.TABLET]: '4ce01fcb480fb79270c3d18dec14f045',
    },
    recipeSourceId: 279,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#4775ff', // Baby Blue
    secondaryColor: 'rgb(254, 224, 207)', // Salmon
    fontFamily: '"Acronym",sans-serif',
});

shoppableSites.set(Site.REAL_SIMPLE, {
    site: Site.REAL_SIMPLE,
    groceryServerEndpoint: 'https://realsimple.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '48c66f9ba763f6c43ff7d91771e1622c',
        [DeviceType.MOBILE]: '2d60e5102ea1f4721ef9101bd57b17ea',
        [DeviceType.TABLET]: '3cd070b5cfbff1c14fcf38b164705c6e',
    },
    recipeSourceId: 269,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#e5106c',
    secondaryColor: '#f9cfe1',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.RACHAEL_RAY_MAG, {
    site: Site.RACHAEL_RAY_MAG,
    groceryServerEndpoint: 'https://rachaelraymag.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        '2496', // Water
        '20382', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '1b5203a86bd71a66abae6216a61cf101',
        [DeviceType.MOBILE]: 'd17e22aa76756fe8f8d168aeb3a9745a',
        [DeviceType.TABLET]: '5f79b1200bd8463b5e16cc94ffcb0bdc',
    },
    recipeSourceId: 278,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#e5106c',
    secondaryColor: '#f9cfe1',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.SOUTHERN_LIVING, {
    site: Site.SOUTHERN_LIVING,
    groceryServerEndpoint: 'https://southernliving.groceryserver.com/groceryserver',
    ignoredIngredientIds: [
        'dfae4acad8dc26b42297119bf0866c55', // Water
        'aa4671be2814999b547f258f7b140d17', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '0918f209ded8bc7a548c855c60463e8a',
        [DeviceType.MOBILE]: 'c154b496f807ec5b190b6f199054b839',
        [DeviceType.TABLET]: '07711db0537267aea2f1aa983d818a7c',
    },
    recipeSourceId: 270,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#99261e',
    secondaryColor: '#f5f6ea',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.SHAPE, {
    site: Site.SHAPE,
    groceryServerEndpoint: 'https://shape.groceryserver.com/groceryserver/',
    ignoredIngredientIds: [
        'dfae4acad8dc26b42297119bf0866c55', // Water
        'aa4671be2814999b547f258f7b140d17', // Ice
    ],
    platformIds: {
        [DeviceType.DESKTOP]: '814affd21a3e68a845dac230b4a5bd2d',
        [DeviceType.MOBILE]: 'b2105a6782827f9728619e07bd7d65a9',
        [DeviceType.TABLET]: '91660aa41b2eb3de5f283e4b7401f0a6',
    },
    recipeSourceId: 276,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#99261e',
    secondaryColor: '#f5f6ea',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

shoppableSites.set(Site.UNKNOWN, {
    site: Site.UNKNOWN,
    groceryServerEndpoint: '',
    ignoredIngredientIds: [],
    platformIds: {
        [DeviceType.DESKTOP]: '',
        [DeviceType.MOBILE]: '',
        [DeviceType.TABLET]: '',
    },
    recipeSourceId: 0,
    release: ReleaseType.DEVELOPMENT,
    primaryColor: '#99261e',
    secondaryColor: '#f5f6ea',
    fontFamily: "'Lagu Sans', 'Arial Narrow', Arial, sans-serif",
});

export {
    ReleaseType,
    Site,
    shoppableSites,
};

export type { IShoppableSite };
